import { useMutation } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import { isDeviceForbiddenError } from '../../../lib/deviceAuthError'

export class InvalidPinError extends Error {
  constructor() { super('invalid_pin') }
}

export class AlreadyConsumedRedemptionError extends Error {
  constructor() { super('already_consumed') }
}

export class InsufficientRewardsError extends Error {
  constructor() { super('insufficient_rewards') }
}

interface RedeemRewardResponse {
  success: boolean
  error?: string
  remainingCount?: number
}

async function redeemReward(redemptionTokenId: string, pin: string, quantity?: number): Promise<number> {
  const { data, error } = await supabase.functions.invoke<RedeemRewardResponse>(
    'redeem-reward',
    { body: { redemptionTokenId, pin, ...(quantity !== undefined && { quantity }) } },
  )

  if (error) {
    if (!isDeviceForbiddenError(error)) reportUnexpectedError(error)
    throw error
  }
  if (!data) {
    const failure = new Error('redeem_failed')
    reportUnexpectedError(failure)
    throw failure
  }

  if (data.error === 'invalid_pin')         throw new InvalidPinError()
  if (data.error === 'already_consumed')    throw new AlreadyConsumedRedemptionError()
  if (data.error === 'insufficient_rewards') throw new InsufficientRewardsError()
  if (!data.success) {
    const failure = new Error(data.error ?? 'redeem_failed')
    reportUnexpectedError(failure)
    throw failure
  }

  return data.remainingCount ?? 0
}

export function useRedeemReward() {
  return useMutation({
    mutationFn: ({ redemptionTokenId, pin, quantity }: { redemptionTokenId: string; pin: string; quantity?: number }) =>
      redeemReward(redemptionTokenId, pin, quantity),
  })
}
